import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { EmailCampaignStatusHTMLSpanDraw, EmailHistoryStatus } from '@models/email/email-issue';
import { filterKey } from '@models/reseller/sms-history-detail';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EmailHistoryService } from '@services/email-history.service';
import { LoaderService } from '@services/loader.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { formateDateTime } from '@utils/utils';
import { GridReadyEvent } from 'ag-grid-community';
import moment from 'moment';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-email-history-sent',
  templateUrl: './email-history-sent.component.html',
  styleUrls: ['./email-history-sent.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class EmailListSentComponent implements OnInit, AfterViewInit {
  @ViewChild('grid') grid: AtlasGridComponent;
  @ViewChild('modalEmailPreview') modalEmailPreview;
  modalEmailPreviewModal: NgbModalRef;

  selectedEmailPreview = '';
  today = new Date();
  dateFrom;
  dateTo;
  filter;

  tableColumnsDefs;
  tableGridOptions;
  tableLoading: boolean;
  tableRowData = null;
  paginationPageSize = 10;
  gridAnimationState = 'hide';
  isLoaderActive = false;

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };
  constructor(
    private emailHistoryService: EmailHistoryService,
    private translateService: TranslateService,
    private loaderService: LoaderService,
    private modalService: NgbModal,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.defineTable();

    this.gridOptionDefinition();
  }

  ngAfterViewInit(): void {
    this.grid.dataIsLoaded.subscribe(() => {
      this.gridAnimationState = 'show';
    });
  }

  defineTable() {
    this.tableColumnsDefs = [
      {
        headerNameTranslate: 'email_campaign_info',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              return this.campaignEmailHtml(params.data);
            }
            return '';
          }
        },
        sortable: false,
        onCellClicked: params => {
          this.openPreviewModal(params.data.idCampaignIssue);
        },
        cellStyle: { 'line-height': '25px' },
        width: 200
      },
      {
        headerNameTranslate: 'email_campaign_status',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            return params && params.data ? this.getHtmlRenderEmailCampaignStatus(params.data) : '';
          }
        },
        suppressCellFocus: true,
        sortable: false,
        width: 120
      },
      {
        headerNameTranslate: 'email_campaign_recipients',
        field: 'countRecipients',
        width: 100,
        cellClass: ['campaign-email-recipients'],
        suppressCellFocus: true,
        sortable: false
      },
      {
        headerNameTranslate: 'email_campaign_percentage',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (!params || !params.data) {
              return '';
            }
            if (params.data.countRecipients) {
              return '<div class="d-flex flex-column"><span class="campaign-email-opens">' + params.data.uniqueOpens + ' %</span></div>';
            }
            return '';
          }
        },
        suppressCellFocus: true,
        width: 100,
        cellClass: ['campaign-email-opens'],
        sortable: false
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          items: [
            {
              onClick: e => this.router.navigate(['/mail/send'], { queryParams: { copy: e.idCampaignIssue } }),
              label: 'common_copy'
            },
            {
              onClick: e => this.openPreviewModal(e.idCampaignIssue),
              label: 'mail_preview'
            },
            {
              label: 'common_stat',
              onClick: e => this.router.navigate(['/mail/stats', e.idCampaignIssue])
            }
          ]
        },

        cellClass: ['button-group', 'text-end'],
        maxWidth: 82,
        field: '__actions'
      }
    ];
  }

  gridOptionDefinition() {
    const that = this;
    const key = 'period';

    this.tableGridOptions = AtlasGridOptions.deserialize({
      onGridReady: (event: GridReadyEvent) => {
        this.filter = this.grid.filtersModel[key];
        this.loadData();
      },
      onFilterChange: (event: any) => {
        if ((event.label as string)?.includes('timefilter')) {
          this.onFilterChange(event.value);
          this.grid.filter();
        } else {
          this.buildDate();
        }
      },
      rowHeight: 110,
      pagination: true,
      resizable: false,
      paginationPageSize: this.paginationPageSize,
      filters: this.getFilters(),
      loadFunction: this.reloadList.bind(that),
      noRowsOverlayOptions: {
        message: ''
      }
    });
    this.reloadList();
  }

  loadData() {
    this.grid.filtersModel[filterKey.period] = {
      label: 'timefilter_dates_all',
      value: 'all',
      translate: true
    };
    this.onFilterChange('all');
    this.grid.refresh();
  }

  reloadList(startRow?, endRow?, sort?) {
    if (!this.isLoaderActive) {
      this.loaderService.show();
      this.isLoaderActive = true;
    }
    return this.emailHistoryService
      .getSmsSentList(
        this.getFilterdStatus(),
        null,
        this.dateFrom,
        this.dateTo,
        null,
        null,
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        'lastSaveTime',
        'd'
      )
      .pipe(
        finalize(() => {
          this.loaderService.hide();
          this.isLoaderActive = false;
        })
      );
  }

  getFilters() {
    return [
      {
        name: 'period',
        label: 'common_from',
        type: 'dropdown',
        options: [
          {
            label: 'timefilter_dates_all',
            value: 'all',
            translate: true
          },
          {
            label: 'timefilter_dates_lasthour',
            value: 'lastHour',
            translate: true
          },
          {
            label: 'timefilter_dates_last24hours',
            value: 'last24hours',
            translate: true
          },
          {
            label: 'timefilter_dates_today',
            value: 'today',
            translate: true
          },
          {
            label: 'timefilter_dates_thisweek',
            value: 'thisweek',
            translate: true
          },
          {
            label: 'timefilter_dates_thismonth',
            value: 'thismonth',
            translate: true
          },
          {
            label: 'timefilter_dates_lastmonth',
            value: 'lastmonth',
            translate: true
          }
        ]
      },
      {
        name: 'fromDate',
        label: 'common_from',
        type: 'date'
      },
      {
        name: 'fromTime',
        label: 'common_from',
        type: 'time'
      },
      {
        name: 'toDate',
        label: 'common_to',
        type: 'date'
      },
      {
        name: 'toTime',
        label: 'common_from',
        type: 'time'
      }
    ];
  }

  onFilterChange(filter) {
    let dateFrom;
    let dateTo;
    let timezone;
    const offset = moment(this.today).utcOffset();

    if (offset < 0) {
      timezone =
        '-' +
        this.today
          .toString()
          .split('-')[1]
          .split(' ')[0];
    } else {
      timezone =
        '+' +
        this.today
          .toString()
          .split('+')[1]
          .split(' ')[0];
    }

    switch (filter) {
      case 'all':
        dateFrom = new Date(null);

        break;
      case 'lastHour':
        dateFrom = moment()
          .subtract(1, 'hour')
          .format();

        break;

      case 'last24hours':
        dateFrom = moment()
          .subtract(24, 'hour')
          .format();

        break;

      case 'today':
        dateFrom = moment()
          .startOf('day')
          .format();

        break;

      case 'thisweek':
        dateFrom = moment()
          .startOf('isoWeek')
          .format();

        break;

      case 'thismonth':
        dateFrom = moment()
          .startOf('month')
          .format();

        break;

      case 'lastmonth':
        dateFrom = moment()
          .startOf('month')
          .subtract(1, 'month')
          .format();
        dateTo = moment()
          .startOf('month')
          .format();
        break;
      default:
        break;
    }

    const newDate = new Date(
      moment(dateFrom)
        .format('ddd MMM DD YYYY HH:mm:ss')
        .toString()
    );

    this.dateFrom =
      moment(dateFrom)
        .format('YYYY-MM-DDTHH:mm:ss')
        .toString() +
      '+' +
      timezone;
    this.dateTo =
      moment()
        .format('YYYY-MM-DDTHH:mm:ss')
        .toString() +
      '+' +
      timezone;

    this.setFilters(newDate, dateFrom, dateTo);
  }

  private setFilters(newDate: Date, dateFrom: any, filterDateTo) {
    let dateTo;
    if (filterDateTo) {
      dateTo = new Date(
        moment(filterDateTo)
          .format('ddd MMM DD YYYY HH:mm:ss')
          .toString()
      );
    }
    const toTime = dateTo ? moment(dateTo).format('HH:mm') : moment(this.today).format('HH:mm');
    this.grid.filtersModel[filterKey.fromDateKey] = newDate;
    this.grid.filtersModel[filterKey.fromTimeKey] = moment(dateFrom).format('HH:mm');
    this.grid.filtersModel[filterKey.toDateKey] = dateTo ? dateTo : this.today;
    this.grid.filtersModel[filterKey.toTimeKey] = toTime;
    this.buildDate();
  }

  buildDate() {
    const offset = moment(this.today).utcOffset();
    let timezone;

    if (offset < 0) {
      timezone =
        '-' +
        this.today
          .toString()
          .split('-')[1]
          .split(' ')[0];
    } else {
      timezone =
        '+' +
        this.today
          .toString()
          .split('+')[1]
          .split(' ')[0];
    }
    const dateFrom =
      moment(this.grid.filtersModel[filterKey.fromDateKey])
        .format()
        .split('T')[0] +
      'T' +
      this.grid.filtersModel[filterKey.fromTimeKey] +
      ':00' +
      timezone;
    const dateTo =
      moment(this.grid.filtersModel[filterKey.toDateKey])
        .format()
        .split('T')[0] +
      'T' +
      this.grid.filtersModel[filterKey.toTimeKey] +
      ':00' +
      timezone;
    this.dateFrom = dateFrom;
    this.dateTo = dateTo;
  }

  getFilterdStatus() {
    return [EmailHistoryStatus.SENT, EmailHistoryStatus.ERROR];
  }

  campaignEmailHtml(campaign) {
    return (
      '<div class="campaign-email-title">' +
      campaign.name +
      '</div>' +
      '<div class="campaign-email-subject">' +
      campaign.subject +
      '</div>' +
      this.getEditedOrSentHtml(campaign) +
      '</div>'
    );
  }

  getEditedOrSentHtml(emailCampaign) {
    const labels = ['mail_list_sent_table_senton', 'mail_send_campaign_input'];
    const sentOn = this.translateService.instant(labels[0]);
    const list = this.translateService.instant(labels[1]);
    if (emailCampaign.sendDate) {
      return (
        '<div class="campaign-email-editDate">' +
        '<span class="sendDate">' +
        sentOn +
        ': </span>' +
        (emailCampaign?.sendDate ? this.getFormattedDateHtml(emailCampaign.sendDate) : '') +
        '<span class="lastSaveTime"> ' +
        list +
        ': </span>' +
        (emailCampaign?.campaign?.title ? emailCampaign.campaign.title : '') +
        '</div>'
      );
    }
    return (
      '<div class="campaign-email-editDate">' +
      '<span class="sendDate">' +
      sentOn +
      ': </span>' +
      (emailCampaign?.lastSaveTime ? this.getFormattedDateHtml(emailCampaign.lastSaveTime) : ' -- ') +
      '<span class="lastSaveTime"> ' +
      list +
      ': </span>' +
      (emailCampaign?.campaign?.title ? emailCampaign.campaign.title : '') +
      '</div>'
    );
  }

  private getHtmlRenderEmailCampaignStatus(emailCampaign) {
    const status = emailCampaign.sendStatus;
    switch (status) {
      case EmailHistoryStatus.DRAFT:
        return this.getHtmlRenderStatus('mail_send_status_0', EmailCampaignStatusHTMLSpanDraw.DRAFT);
      case EmailHistoryStatus.SENT:
        return this.getHtmlRenderStatus('mail_send_status_2', EmailCampaignStatusHTMLSpanDraw.SENT);
      case EmailHistoryStatus.ERROR:
        return this.getHtmlRenderStatus('mail_send_status_5', EmailCampaignStatusHTMLSpanDraw.ERROR);
    }
  }

  private getHtmlRenderStatus(label, spanHTML) {
    const labelStatus = this.getTranslateLabels(label);
    return spanHTML + labelStatus + '</span>';
  }

  private getTranslateLabels(value: string) {
    if (value) {
      let labels;
      this.translateService.get(value).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }

  private getFormattedDateHtml(date) {
    const dateTime = formateDateTime(date).split(' ');
    const newDate = dateTime[0];
    const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
    return '<span>' + newDate + '</span> - <span>' + time + '</span>';
  }

  private openPreviewModal(emailCampaignId) {
    this.emailHistoryService.getEmailPreview(emailCampaignId).subscribe(issue => {
      this.selectedEmailPreview = issue.htmlBody;
      this.modalEmailPreviewModal = this.modalService.open(this.modalEmailPreview, { size: 'xl' });

      this.modalEmailPreviewModal.result.then(
        result => {},
        reason => {}
      );
    });
  }
}
